<!-- eslint-disable vue/no-v-html -->
<template>
	<section class="container py-5">
		<div class="row align-items-center">
			<div class="col-lg-6 text-start pe-lg-5">
				<!-- Multi-section (Mind) -->
				<template v-if="hasSections">
					<div
						v-for="(section, idx) in sectionsText"
						:key="'benefit-section-' + idx"
						class="mb-4">
						<h1
							class="bolder-text mb-3"
							v-html="section.title" />
						<p
							v-if="section.description"
							class="mb-0"
							v-html="section.description" />
						<ul
							v-else
							class="list-unstyled mb-0">
							<li
								v-for="(bullet, i) in section.bullets"
								:key="`benefit-sec-${idx}-bullet-${i}`"
								class="d-flex align-items-start mb-2">
								<span
									class="me-3 d-inline-flex align-items-center justify-content-center"
									aria-hidden="true">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="20"
										height="20"
										viewBox="0 0 20 20">
										<path
											id="bullet"
											d="M10,20A10,10,0,1,0,0,10,10,10,0,0,0,10,20ZM14.414,8.164l-5,5a.934.934,0,0,1-1.324,0l-2.5-2.5A.936.936,0,0,1,6.914,9.34L8.75,11.176l4.336-4.34A.936.936,0,0,1,14.41,8.16Z"
											:fill="bulletColor" />
									</svg>&nbsp;
								</span>
								<span v-html="bullet" />
							</li>
						</ul>
					</div>
				</template>

				<!-- Single-section (Reserve) -->
				<template v-else>
					<h1
						class="bolder-text mb-3"
						v-html="singleText.title" />
					<ul class="list-unstyled mb-4">
						<li
							v-for="(bullet, i) in singleText.bullets"
							:key="'benefit-bullet-' + i"
							class="d-flex align-items-start mb-2">
							<span
								class="me-3 d-inline-flex align-items-center justify-content-center"
								aria-hidden="true">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="20"
									height="20"
									viewBox="0 0 20 20">
									<path
										id="bullet"
										d="M10,20A10,10,0,1,0,0,10,10,10,0,0,0,10,20ZM14.414,8.164l-5,5a.934.934,0,0,1-1.324,0l-2.5-2.5A.936.936,0,0,1,6.914,9.34L8.75,11.176l4.336-4.34A.936.936,0,0,1,14.41,8.16Z"
										:fill="bulletColor" />
								</svg>&nbsp;
							</span>
							<span v-html="bullet" />
						</li>
					</ul>
					<!-- Reserve-specific separator and H1 subtitle -->
					<div
						v-if="isReserve"
						class="d-flex flex-column align-items-center text-center">
						<div class="benefits-separator my-4" />
						<h1
							class="bolder-text mb-0 custom-width"
							v-html="singleText.subtitle" />
					</div>
					<p
						v-if="!isReserve && singleText.subtitle"
						class="mb-0"
						v-html="singleText.subtitle" />
					<!-- Optional usage block -->
					<div
						v-if="usageText.title || usageText.description"
						class="mt-4">
						<h2
							class="h5 bolder-text mb-2"
							v-html="usageText.title" />
						<p
							class="mb-0"
							v-html="usageText.description" />
					</div>

					<!-- Optional quote block -->
					<blockquote
						v-if="quoteText"
						class="mt-4 mb-0 fst-italic"
						v-html="quoteText" />
				</template>
			</div>

			<!-- RIGHT: Image -->
			<div class="col-lg-6 text-center mt-4 mt-lg-0">
				<img
					v-if="imageSrc"
					:src="requireAsset(imageSrc)"
					alt="Benefits"
					class="img-fluid benefits-image">
			</div>
		</div>
	</section>
</template>

<script>
import { JnsLandingPages } from '@/translations';
import { PRODUCT_PAGES } from '@/settings/JnsLandingPages';
import Page from '@/views/JnsLandingPages/mixins/Page';
import Storage from '@/views/JnsLandingPages/mixins/Storage';
import Replacements from '@/views/JnsLandingPages/mixins/Replacements';

export default {
	name: 'BenefitsBreakdown',
	messages: [JnsLandingPages],
	mixins: [Page, Storage, Replacements],
	data() {
		return {
			bulletColor: '#84286C',
		};
	},
	computed: {
		cfg() {
			return PRODUCT_PAGES[this.pageName]?.benefitsBreakdown || {};
		},
		hasSections() {
			return Array.isArray(this.cfg.sections) && this.cfg.sections.length > 0;
		},
		translateText() {
			return (key) => this.applyReplacements(this.translate(key || ''));
		},
		translateList() {
			return (keys = []) => keys.map((k) => this.translateText(k)).filter(Boolean);
		},
		isReserve() {
			return (this.pageName || '').toLowerCase() === 'reserve';
		},
		singleText() {
			if (this.hasSections) return {};
			const c = this.cfg;
			return {
				title: this.translateText(c.title),
				bullets: this.translateList(c.bullets),
				subtitle: this.translateText(c.subtitle),
			};
		},
		sectionsText() {
			if (!this.hasSections) return [];
			return (this.cfg.sections || []).map((sec) => ({
				title: this.translateText(sec.title),
				description: this.translateText(sec.description),
				bullets: this.translateList(sec.bullets),
			}));
		},
		usageText() {
			return {
				title: this.translateText(this.cfg.usage?.title),
				description: this.translateText(this.cfg.usage?.description),
			};
		},
		quoteText() {
			return this.translateText(this.cfg.quote);
		},
		imageSrc() {
			return this.cfg.image || '';
		},
	},
	mounted() {
		try {
			const colorFromHere = getComputedStyle(this.$el).getPropertyValue('--jlp-primary-color').trim();
			if (colorFromHere) {
				this.bulletColor = colorFromHere;
				return;
			}
			const colorFromDoc = getComputedStyle(document.documentElement).getPropertyValue('--jlp-primary-color').trim();
			if (colorFromDoc) {
				this.bulletColor = colorFromDoc;
			}
		} catch (e) {
			// donothing
		}
	},
};
</script>

<style scoped>
.bolder-text {
	font-weight: bolder;
}
.benefits-image {
	max-width: 100%;
	height: auto;
}
.benefits-separator {
	border-top: 2px solid var(--jlp-primary-color);
	width: 25%;
}
.custom-width {
	width: 65%;
}
</style>
